import * as React from "react";
import * as _ from "lodash";
import {translate} from "react-i18next";

// services
import {AssignedUserRoleDto, RoleDto, UserDto,} from "../../../services/Client";

// reducer
import {ITranslatedComponent} from "../../../redux/reducers/generalReducer";

// semantic
import {Modal, Table} from "semantic-ui-react";

import Button from "../../../components/Button";
import Textfield from "../../../components/TextField";
import DeputySelection from "./DeputySelection";
import AssignedRoleSelection, {RoleAssignmentType} from "./AssignedRoleSelection";

export interface IUserCreationDialogProps extends ITranslatedComponent {
    /** All existing users. Used to check for duplicates. */
    users: UserDto[];

    /** All available roles. Possible roles for the new user. */
    roles: RoleDto[];

    /** Callback when the user closes the dialog. */
    onClose?: (newUser?: UserDto) => any;
}

interface IUserCreationDialogState {
    /** The name (cn) of the new user. */
    name: string;

    /** The set of roles of the new user. */
    assignedRoles: AssignedUserRoleDto[];

    /** Optionally specify a deputy when creating the new user. */
    deputy?: UserDto;

    /** Optionally copy uesr settings from an existing user. */
    userSettings?: string;
}

/**
 * The UserCreationDialog collects all necessary information for
 * creating a new user. The component doesn't create the user itself
 * but passes a UserDto to it's client through onClose() which is
 * expected to call the back end API to create the user.
 */
export class UserCreationDialog extends React.Component<IUserCreationDialogProps, IUserCreationDialogState> {
    readonly dummyUserForAssignedRole: UserDto = {
        cn: "",
        objectId: 0,
        versionId: 0
    };

    constructor(props: IUserCreationDialogProps) {
        super(props);

        this.state = {
            name: "",
            assignedRoles: [],
            deputy: undefined,
            userSettings: undefined,
        };
    }

    validateInput = (): boolean => {
        const containsUser = (name: string, users: UserDto[]): boolean => {
            return _.find(users, user => user.cn.toLowerCase() === this.state.name.toLowerCase()) !== undefined;
        };

        // TODO: check the name for proper format&characters!
        const nameOk = Boolean(this.state.name) && !containsUser(this.state.name, this.props.users);
        const rolesOk = this.state.assignedRoles.length !== 0;
        const deputyOk = true; // TODO: check no deputy cycle
        return nameOk && rolesOk && deputyOk;
    }

    onClickCreate = (): void => {
        if (this.props.onClose) {
            // Each AssignedUserRoleDto requires a reference of the user it is asigned
            // to. But when creating a new user we don't have that DTO. In this case
            // the back end is satisfied with an empty DTO.
            const newUser: UserDto = {
                cn: this.state.name,
                assignedRoles: this.state.assignedRoles,
                deputy: this.state.deputy,
                userSettings: this.state.userSettings,
                objectId: 0, // dummy objectId for creation
                versionId: 0, // dummy versionId for creation
            };
            this.props.onClose(newUser);
        }
    }

    onClickCancel = (): void => {
        if (this.props.onClose) {
            this.props.onClose();
        }
    }

    renderNameControl = (): JSX.Element => (
        <Textfield
            placeholder={this.props.t("ENTER_USER_NAME")}
            onChange={(event: React.FormEvent<{}>, newValue: string) => {
                this.setState({name: newValue});
            }}
        />
    )

    renderDeputyControl = (): JSX.Element => (
        <DeputySelection
            placeholder={this.props.t("SELECT_DEPUTY")}
            users={this.props.users}
            onChange={(user: UserDto | undefined) => this.setState({deputy: user})}
        />
    )

    renderRolesControl = (): JSX.Element => (
        <AssignedRoleSelection
            roles={this.props.roles}
            roleAssignType={RoleAssignmentType.USER}
            parentDto={this.dummyUserForAssignedRole}
            values={this.state.assignedRoles}
            onChange={(assignedRoles: AssignedUserRoleDto[]) => {
                this.dummyUserForAssignedRole.assignedRoles = assignedRoles;
                this.setState({assignedRoles: assignedRoles});
            }}
        />
    )

    renderContent = (): JSX.Element => (
        <>
            <Table definition={true}>
                <Table.Body>
                    <Table.Row>
                        <Table.Cell>{this.props.t("USER_NAME")}*</Table.Cell>
                        <Table.Cell>{this.renderNameControl()}</Table.Cell>
                    </Table.Row>
                    <Table.Row>
                        <Table.Cell>{this.props.t("ROLES")}*</Table.Cell>
                        <Table.Cell>{this.renderRolesControl()}</Table.Cell>
                    </Table.Row>
                    <Table.Row>
                        <Table.Cell>{this.props.t("DEPUTY")}</Table.Cell>
                        <Table.Cell>{this.renderDeputyControl()}</Table.Cell>
                    </Table.Row>
                </Table.Body>
            </Table>
            * {this.props.t("MANDATORY_FIELD")}
        </>
    )

    render() {
        return (
            <Modal open={true} style={{"marginTop": 0}}>
                <Modal.Header>{this.props.t("ADD_USER")}</Modal.Header>
                <Modal.Content>
                    {this.renderContent()}
                </Modal.Content>
                <Modal.Actions>
                    <Button
                        icon={"checkmark"}
                        disabled={!this.validateInput()}
                        onClick={this.onClickCreate}
                    />
                    <Button
                        icon={"ban"}
                        onClick={this.onClickCancel}
                    />
                </Modal.Actions>
            </Modal>
        );
    }
}

export default translate("ADMIN_VIEW")(UserCreationDialog);



// WEBPACK FOOTER //
// ./src/views/AdminView/AccessTab/UserCreationDialog.tsx